import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Clock, 
  Calendar, 
  User, 
  Share2, 
  Tag, 
  Sparkles, 
  Check, 
  ShoppingBag, 
  ArrowRight,
  ArrowLeft,
  Eye,
  MessageCircle,
  Copy,
  ChevronLeft,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/formatters';

const ArticleDetailPage = ({ 
  article, 
  onBackToHome, 
  onBackToBlogs, 
  onSelectArticle,
  onSelectProduct 
}) => {
  const { products, blogs, settings, incrementBlogViews } = useStore();
  const { addToCart, setIsCheckoutOpen, viewProductDetail } = useCart();
  const [copied, setCopied] = useState(false);
  const [addedToCartSuccess, setAddedToCartSuccess] = useState(false);

  useEffect(() => {
    if (article?.id) {
      incrementBlogViews(article.id);
      // Update browser URL query parameter
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('article', article.slug || article.id);
        url.searchParams.delete('product');
        window.history.pushState({}, '', url);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [article?.id]);

  if (!article) return null;

  // Find linked product if specified
  const linkedProduct = article.linkedProductId 
    ? products.find(p => p.id === article.linkedProductId)
    : null;

  // Find related articles (same category or others, excluding current)
  const relatedArticles = blogs
    .filter(b => b.status === 'published' && b.id !== article.id)
    .sort((a, b) => (a.category === article.category ? -1 : 1))
    .slice(0, 3);

  // Article Share URL
  const currentUrl = typeof window !== 'undefined' 
    ? window.location.origin + window.location.pathname + `?article=${article.slug || article.id}` 
    : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShareWhatsApp = () => {
    const text = `🌿 *${article.title}*\n\n${article.excerpt || ''}\n\n📖 مکمل طبی مضمون پڑھیں:\n${currentUrl}\n\nشکریہ: ${settings.storeNameUrdu || 'ہمالین ہربل اسٹور'}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleAddToCart = (prod) => {
    addToCart(prod, prod.variants?.[0], 1);
    setAddedToCartSuccess(true);
    setTimeout(() => setAddedToCartSuccess(false), 2000);
  };

  const handleBuyNow = (prod) => {
    addToCart(prod, prod.variants?.[0], 1);
    if (setIsCheckoutOpen) {
      setIsCheckoutOpen(true);
    }
  };

  const handleViewProduct = (prod) => {
    if (onSelectProduct) {
      onSelectProduct(prod);
    } else if (viewProductDetail) {
      viewProductDetail(prod);
    }
  };

  // Structured Data Schema for SEO
  const displayTitle = article.titleUrdu || article.title || '';
  const displayExcerpt = article.excerptUrdu || article.excerpt || article.metaDescription || '';
  const displayImage = article.coverImage || article.featuredImage || linkedProduct?.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1200&auto=format&fit=crop&q=80';
  const displayContent = article.contentHtml || article.content || '';
  const displayCategory = article.categoryUrdu || article.category || 'جڑی بوٹیاں';

  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": displayTitle,
    "description": displayExcerpt,
    "image": displayImage,
    "author": {
      "@type": "Person",
      "name": article.author || "حکیم مولانا سید عبدالوہاب شاہ شیرازی"
    },
    "publisher": {
      "@type": "Organization",
      "name": settings.storeNameUrdu || "ہمالین پنسار سٹور",
      "logo": {
        "@type": "ImageObject",
        "url": settings.logoUrl || ""
      }
    },
    "datePublished": article.publishedDate || article.createdAt,
    "mainEntityOfPage": currentUrl,
    "keywords": article.focusKeywords
  };

  return (
    <div className="min-h-screen bg-gray-50/70 pb-20 pt-4 sm:pt-6 font-urdu" dir="rtl">
      
      {/* Dynamic SEO JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdData) }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
        
        {/* Top Breadcrumb Navigation Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-2xl border border-gray-200/80 shadow-xs text-xs sm:text-sm">
          <nav className="flex items-center gap-1.5 sm:gap-2 text-gray-500 overflow-x-auto whitespace-nowrap scrollbar-none">
            <button
              type="button"
              onClick={onBackToHome}
              className="hover:text-emerald-800 font-bold transition flex items-center gap-1 cursor-pointer"
            >
              <span>صفحہ اول</span>
            </button>
            <span className="text-gray-300">/</span>
            <button
              type="button"
              onClick={onBackToBlogs}
              className="hover:text-emerald-800 font-bold transition cursor-pointer"
            >
              <span>بلاگ لائبریری</span>
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-emerald-800 font-medium">
              {displayCategory}
            </span>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-bold truncate max-w-[180px] sm:max-w-none">
              {displayTitle}
            </span>
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyLink}
              title="مضمون کا لنک کاپی کریں"
              className="p-2 rounded-xl bg-gray-100 hover:bg-emerald-50 hover:text-emerald-800 text-gray-600 transition flex items-center gap-1.5 text-xs cursor-pointer"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{copied ? 'کاپی ہو گیا!' : 'لنک کاپی کریں'}</span>
            </button>

            <button
              type="button"
              onClick={handleShareWhatsApp}
              title="واٹس ایپ پر شیئر کریں"
              className="p-2 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-900 transition flex items-center gap-1.5 text-xs font-bold cursor-pointer"
            >
              <MessageCircle className="w-4 h-4 text-emerald-700" />
              <span className="hidden sm:inline">شیئر کریں</span>
            </button>

            <button
              type="button"
              onClick={onBackToBlogs}
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200 transition cursor-pointer"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              <span>تمام مضامین</span>
            </button>
          </div>
        </div>

        {/* Main Article Container */}
        <article className="bg-white rounded-3xl p-6 sm:p-10 lg:p-12 border border-gray-200/90 shadow-sm space-y-8">
          
          {/* Article Header Details */}
          <div className="space-y-4 pb-6 border-b border-gray-150">
            
            {/* Category & Read Time Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold border border-emerald-200">
                <Tag className="w-3.5 h-3.5 text-emerald-700" />
                <span>{displayCategory}</span>
              </span>

              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">
                <Clock className="w-3.5 h-3.5 text-emerald-600" />
                <span>{article.readTime || '5 منٹ کا مطالعہ'}</span>
              </span>
            </div>

            {/* Article Main H1 Title */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 leading-tight">
              {displayTitle}
            </h1>

            {/* Author & Publish Date & Views */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-emerald-900 font-bold bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200">
                  <User className="w-4 h-4 text-emerald-700" />
                  <span>{article.author || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی'}</span>
                </div>

                <div className="flex items-center gap-1.5 text-gray-500 font-sans">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{article.publishedDate || article.createdAt}</span>
                </div>
              </div>

              <div className="flex items-center gap-1.5 text-gray-400 font-sans bg-gray-50 px-3 py-1 rounded-lg">
                <Eye className="w-3.5 h-3.5 text-gray-500" />
                <span>{article.viewsCount || article.views || 1} مشاہدات (Views)</span>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          {displayImage && (
            <div className="rounded-3xl overflow-hidden shadow-lg border border-gray-200 max-h-[460px] relative bg-emerald-950">
              <img
                src={displayImage}
                alt={displayTitle}
                className="w-full h-full object-cover object-center max-h-[460px]"
                onError={(e) => { 
                  e.currentTarget.onerror = null; 
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1200&auto=format&fit=crop&q=80'; 
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            </div>
          )}

          {/* Lead Excerpt */}
          {displayExcerpt && (
            <div className="p-5 sm:p-6 rounded-2xl bg-emerald-50/70 border-r-4 border-emerald-700 text-sm sm:text-base text-emerald-950 leading-relaxed font-semibold">
              {displayExcerpt}
            </div>
          )}

          {/* Main Rich Article HTML Content */}
          <div 
            className="prose prose-emerald max-w-none text-gray-800 text-sm sm:text-base leading-relaxed sm:leading-loose font-urdu article-content-body space-y-4"
            dangerouslySetInnerHTML={{ __html: displayContent }}
          />

          {/* Topics & Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="pt-6 border-t border-gray-150 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-gray-600 ml-2">موضوعات و ٹیگز:</span>
              {article.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs border border-gray-200">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Linked Product Card (Direct Conversion to Store!) */}
          {linkedProduct && (
            <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-emerald-900 via-emerald-850 to-pansar-900 text-white shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-64 h-64 bg-emerald-700/20 rounded-full blur-2xl pointer-events-none" />
              
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 justify-between">
                
                <div className="flex items-center gap-4 w-full md:w-auto">
                  <img
                    src={linkedProduct.image}
                    alt={linkedProduct.nameUrdu}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-emerald-500/50 shrink-0 shadow-md"
                  />
                  <div>
                    <div className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300 bg-emerald-800/90 px-2 py-0.5 rounded mb-1">
                      <Sparkles className="w-3 h-3" />
                      <span>مضمون سے متعلقہ خالص دوا</span>
                    </div>
                    <h3 className="font-bold text-lg sm:text-xl text-white">
                      {linkedProduct.nameUrdu}
                    </h3>
                    <p className="text-xs text-emerald-200 font-sans mt-0.5">
                      {linkedProduct.nameEn}
                    </p>
                    <div className="text-base sm:text-lg font-black text-amber-300 font-sans mt-1">
                      {formatPrice(linkedProduct.variants?.[0]?.price || linkedProduct.price, 'ur')}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
                  <button
                    type="button"
                    onClick={() => handleViewProduct(linkedProduct)}
                    className="flex-1 md:flex-initial px-5 py-2.5 rounded-xl bg-emerald-800 hover:bg-emerald-700 text-emerald-100 text-xs font-bold transition cursor-pointer"
                  >
                    مکمل تفصیل دیکھیں
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAddToCart(linkedProduct)}
                    className="flex-1 md:flex-initial px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-gray-950 font-black text-xs sm:text-sm shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {addedToCartSuccess ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-gray-950" />
                        <span>کارٹ میں شامل ہو گیا!</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        <span>کارٹ میں شامل کریں</span>
                      </>
                    )}
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* Author Box */}
          <div className="p-6 sm:p-8 rounded-3xl bg-gray-50 border border-gray-200 flex flex-col sm:flex-row items-center gap-5 text-right">
            <div className="w-18 h-18 rounded-2xl bg-emerald-800 text-white flex items-center justify-center font-bold text-2xl shrink-0 shadow-md font-urdu">
              شیرازی
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-base sm:text-lg text-gray-900">
                  {article.author || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی'}
                </h4>
                <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full">
                  طبی نگراں و مصنف
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                فاضل طب و الجراحت (FTJ)، ماہر نبض و غذائی علاج، 20 سالہ کلینیکل تجربہ۔ حضرت صابر ملتانیؒ کے اصولوں اور خالص نباتاتی ادویات کے ذریعے انسانیت کی بے لوث خدمت کے لیے کوشاں۔
              </p>
            </div>
          </div>

          {/* Share Actions Bottom Bar */}
          <div className="pt-6 border-t border-gray-150 flex flex-wrap items-center justify-between gap-4">
            <button
              type="button"
              onClick={onBackToBlogs}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-200 transition cursor-pointer"
            >
              <ArrowRight className="w-4 h-4" />
              <span>تمام طبی مضامین و بلاگ لائبریری</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyLink}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-700" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? 'لنک کاپی ہو گیا!' : 'مضمون کا لنک کاپی کریں'}</span>
              </button>

              <button
                type="button"
                onClick={handleShareWhatsApp}
                className="px-5 py-2 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <MessageCircle className="w-4 h-4 text-emerald-300" />
                <span>واٹس ایپ پر شیئر کریں</span>
              </button>
            </div>
          </div>

        </article>

        {/* Related Articles Section */}
        {relatedArticles.length > 0 && (
          <div className="space-y-6 pt-4">
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <div className="text-right">
                <h3 className="text-xl sm:text-2xl font-black text-gray-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-700" />
                  <span>مزید مفید طبی مقالات و مضامین</span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  اسی موضوع اور شعبے سے متعلق دیگر منتخب تحریریں
                </p>
              </div>

              <button
                type="button"
                onClick={onBackToBlogs}
                className="text-xs font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 px-3.5 py-1.5 rounded-xl border border-emerald-200 cursor-pointer"
              >
                تمام مضامین دیکھیں
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((rel) => (
                <div
                  key={rel.id}
                  onClick={() => {
                    if (onSelectArticle) {
                      onSelectArticle(rel);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-emerald-600/50 hover:shadow-lg transition cursor-pointer group flex flex-col justify-between"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-emerald-950 relative">
                    <img 
                      src={rel.featuredImage || "https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=600&auto=format&fit=crop&q=80"} 
                      alt={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2.5 right-2.5">
                      <span className="px-2.5 py-0.5 rounded-full bg-emerald-900/90 text-emerald-100 text-[10px] font-bold">
                        {rel.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-4 space-y-2 flex-1 flex flex-col justify-between text-right">
                    <div>
                      <h4 className="font-bold text-xs sm:text-sm text-gray-900 group-hover:text-emerald-800 transition line-clamp-2">
                        {rel.title}
                      </h4>
                      <p className="text-[11px] text-gray-500 line-clamp-2 mt-1">
                        {rel.excerpt}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-gray-100 text-[10px] text-gray-400 flex items-center justify-between">
                      <span>{rel.readTime || '4 منٹ'}</span>
                      <span className="text-emerald-700 font-bold flex items-center gap-0.5 group-hover:text-emerald-900">
                        <span>پڑھیں</span>
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ArticleDetailPage;
